import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import { PageHeader, Card, Button, Input, Table, LoadingSpinner, EmptyState, Badge, Pagination, Modal } from '../components/UI'; // Added Pagination
import Select from '../components/Select'; // Added Select
import { toast } from 'react-toastify'; // Added toast
import 'react-toastify/dist/ReactToastify.css'; // Added toast CSS
import * as I from 'lucide-react';

const ApprovalPage = ({ navigate, setSelectedInvoice }) => {
    const [approvals, setApprovals] = useState([]); // Represents invoices fetched for approval
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('PENDING_APPROVAL'); // Default filter: show pending approvals
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalApprovals, setTotalApprovals] = useState(0); // Total items from API
    const [pageSize] = useState(10);
    const [vendorMap, setVendorMap] = useState({});
    
    // Edit modal state
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingInvoice, setEditingInvoice] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    
    // TDS Rules state
    const [tdsRules, setTdsRules] = useState([]);
    const [loadingTdsRules, setLoadingTdsRules] = useState(false);
    
    // Form state for TDS update
    const [tdsFormData, setTdsFormData] = useState({
        tdsRuleId: '',
        tdsPercentage: 0
    });

    // Fetch vendor names with batch processing and fallback
    const fetchVendorNames = useCallback(async (vendorIds) => {
        try {
            // Filter out IDs that are null/undefined or already in the map
            const uniqueVendorIds = [...new Set(vendorIds.filter(id => id && !vendorMap[id]))];
            
            if (uniqueVendorIds.length === 0) {
                console.log('No new vendor names to fetch.');
                return;
            }

            console.log('Fetching vendor names for IDs:', uniqueVendorIds);

            // Get the JWT token from localStorage
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No authentication token found');
                return;
            }

            // Create a config object with the Authorization header
            const config = {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            };

            try {
                // First try to fetch all vendors and create a map
                const response = await api.get('/vendors', config);
                console.log('Vendors response:', response.data);
                
                if (response.data && Array.isArray(response.data.content)) {
                    const vendorMapFromResponse = response.data.content.reduce((acc, vendor) => {
                        if (vendor.id && vendor.name) {
                            acc[vendor.id] = vendor.name;
                        }
                        return acc;
                    }, {});
                    
                    console.log('Created vendor map from response:', vendorMapFromResponse);
                    
                    // Filter to only include the vendors we need
                    const newVendorMap = {};
                    uniqueVendorIds.forEach(id => {
                        if (vendorMapFromResponse[id]) {
                            newVendorMap[id] = vendorMapFromResponse[id];
                        } else {
                            console.warn(`Vendor ${id} not found in the vendors list`);
                            newVendorMap[id] = `Vendor ${id}`;
                        }
                    });
                    
                    if (Object.keys(newVendorMap).length > 0) {
                        console.log('Updating vendorMap with new entries:', newVendorMap);
                        setVendorMap(prev => ({
                            ...prev,
                            ...newVendorMap
                        }));
                    }
                } else {
                    throw new Error('Invalid vendors response format');
                }
            } catch (error) {
                console.error('Error fetching vendors:', error?.response?.data || error.message);
                
                // Fallback to individual requests if batch fails
                console.log('Falling back to individual vendor requests...');
                const vendorPromises = uniqueVendorIds.map(id => 
                    api.get(`/vendors/${id}`, config)
                        .then(response => ({
                            id: id.toString(),
                            name: response.data?.name || `Vendor ${id}`
                        }))
                        .catch(() => ({
                            id: id.toString(),
                            name: `Vendor ${id}`
                        }))
                );
                
                const vendorResults = await Promise.all(vendorPromises);
                const fallbackVendorMap = {};
                
                vendorResults.forEach(({ id, name }) => {
                    fallbackVendorMap[id] = name;
                });
                
                if (Object.keys(fallbackVendorMap).length > 0) {
                    console.log('Updating vendorMap with fallback entries:', fallbackVendorMap);
                    setVendorMap(prev => ({
                        ...prev,
                        ...fallbackVendorMap
                    }));
                }
            }
        } catch (err) {
            // This outer catch is for systemic errors
            console.error('A critical error occurred in fetchVendorNames:', err);
        }
    }, [vendorMap]);

    // Define the InvoiceStatus enum to match the backend
    const InvoiceStatus = {
        PENDING_APPROVAL: 'PENDING_APPROVAL',
        APPROVED: 'APPROVED',
        REJECTED: 'REJECTED',
        PAID: 'PAID',
        CANCELLED: 'CANCELLED'
    };

    // Map API status to consistent format used in frontend
    const mapInvoiceStatus = (status) => {
        if (!status) return 'PENDING_APPROVAL'; // Default if status is null/undefined
        const upperStatus = status.toString().toUpperCase();
        
        // Map all possible status values to their display format
        const statusMap = {
            'PENDING_APPROVAL': 'PENDING_APPROVAL',
            'PENDING': 'PENDING_APPROVAL', // Alias
            'APPROVED': 'APPROVED',
            'REJECTED': 'REJECTED',
            'PAID': 'PAID'
        };
        return statusMap[upperStatus] || upperStatus; // Return the status in the same format as backend
    };

    // Convert frontend status to backend enum value
    const toBackendStatus = (status) => {
        if (!status || status === 'all') return null; // Return null for 'all' or empty status
        
        // Map frontend status to backend status (case-insensitive)
        const statusMap = {
            'pending_approval': 'PENDING_APPROVAL',
            'pending': 'PENDING_APPROVAL',
            'approved': 'APPROVED',
            'rejected': 'REJECTED',
            'paid': 'PAID'
        };
        
        // Convert input to string and lowercase for case-insensitive comparison
        const normalizedStatus = status.toString().toLowerCase();
        return statusMap[normalizedStatus] || status.toUpperCase();
    };

    // Get TDS rule description from ID or value
    const getTdsRuleDescription = useCallback((tdsRuleValue) => {
        if (!tdsRuleValue) return 'N/A';

        // Try to find by ID first (if rules are loaded)
        if (tdsRules.length > 0) {
            const rule = tdsRules.find(r => r.id.toString() === tdsRuleValue.toString());
            if (rule) {
                return rule.description;
            }
        }

        // If not found by ID, return the value as is (likely already a description)
        return tdsRuleValue;
    }, [tdsRules]);

    // Fetch invoices needing approval
    const fetchApprovals = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            params.append('page', currentPage);
            params.append('size', pageSize);

            // Always filter for pending approvals only
            params.append('status', 'PENDING_APPROVAL');

            // Sort by Date Received
            params.append('sort', 'dateReceived,desc'); // Or asc if preferred

            const response = await api.get(`/invoices?${params.toString()}`);

            const responseData = response.data.content || [];
            const totalElements = response.data.totalElements || 0;
            const totalPagesResult = response.data.totalPages || 0;

            // Map API response to the structure needed for the table
            const approvalData = responseData.map(inv => ({
                id: inv.id,
                invoiceNumber: inv.invoiceNumber,
                vendorId: inv.vendorId,
                amount: inv.totalAmount || 0,
                status: mapInvoiceStatus(inv.status),
                dateReceived: inv.dateReceived || inv.receivedDateTime || null,

                invoiceDate: inv.invoiceDate || null,
                dueDate: inv.dueDate || null,
                subtotal: inv.subtotal ?? 0,
                tax: inv.tax ?? 0,
                tdsPercentage: inv.tdsPercentage || 0,
                tdsAmount: inv.tdsAmount || 0,
                tdsRule: inv.tdsRule,
                invoicePayableValue: inv.invoicePayable || 0,
                payment: inv.payment,
                tdsRuleId: inv.tdsRuleId
            }));

            setApprovals(approvalData);
            setTotalApprovals(totalElements);
            setTotalPages(totalPagesResult);

             const vendorIds = approvalData.map(a => a.vendorId).filter(Boolean);
             if (vendorIds.length > 0) {
                 fetchVendorNames(vendorIds);
             }

        } catch (err) {
            setError('Failed to fetch approvals. Please ensure backend services are running.');
            console.error("Fetch Approvals Error:", err);
            setApprovals([]);
            setTotalApprovals(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, filterStatus, fetchVendorNames]);

     // Main effect for fetching data
     useEffect(() => {
        fetchApprovals();
    }, [fetchApprovals]);

    // Fetch TDS Rules
    const fetchTdsRules = useCallback(async () => {
        setLoadingTdsRules(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }
            
            const response = await api.get('/tds-rules', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const rules = Array.isArray(response.data) ? response.data : [];
            setTdsRules(rules);
        } catch (error) {
            console.error('Error fetching TDS rules:', error);
            if (error.response?.status === 401) {
                localStorage.removeItem('token');
                navigate('/login');
            } else {
                toast.error(error.response?.data?.message || 'Failed to fetch TDS rules');
            }
        } finally {
            setLoadingTdsRules(false);
        }
    }, [navigate]);

    // Client-side search filtering
    const filteredApprovals = React.useMemo(() => {
        return approvals.filter(approval => {
            const vendorName = vendorMap[approval.vendorId] || '';
            if (!searchTerm) return true;
            const lowerSearchTerm = searchTerm.toLowerCase();
            return (
                (approval.invoiceNumber && approval.invoiceNumber.toLowerCase().includes(lowerSearchTerm)) ||
                (vendorName && vendorName.toLowerCase().includes(lowerSearchTerm))
            );
        });
    }, [approvals, searchTerm, vendorMap]);

    const handlePageChange = (newPage) => {
        // Convert from 1-based (UI) to 0-based (API)
        setCurrentPage(newPage - 1);
    };

    const handleFilterChange = (e) => {
        const newStatus = e.target.value;
        setFilterStatus(newStatus);
        setCurrentPage(0); // Reset page when filter changes
    };

    // Handle TDS percentage update
    const handleEditClick = useCallback((invoice) => {
        setEditingInvoice(invoice);
        // Set initial form data from the invoice object
        // Check both tdsRule and tdsRuleId for backward compatibility
        setTdsFormData({
            tdsRuleId: invoice.tdsRule || invoice.tdsRuleId || '',
            tdsPercentage: invoice.tdsPercentage || 0
        });
        // Fetch TDS rules if not already loaded
        if (tdsRules.length === 0) {
            fetchTdsRules();
        }
        setIsEditModalOpen(true);
    }, [tdsRules, fetchTdsRules]);

    const handleTdsUpdate = async (e) => {
        e.preventDefault();
        if (!editingInvoice || !tdsFormData.tdsRuleId) {
            toast.error('Please select a valid TDS tax section for this invoice');
            return;
        }

        setIsUpdating(true);
        try {
            const token = localStorage.getItem('token');
            // Find the selected rule with loose comparison to handle string/number IDs
            const selectedRule = tdsRules.find(rule => 
                rule.id.toString() === tdsFormData.tdsRuleId.toString() || 
                rule.id === tdsFormData.tdsRuleId
            );
            
            if (!selectedRule) {
                toast.error('Selected TDS tax rule not found for this invoice');
                setIsUpdating(false);
                return;
            }

            const tdsPercentage = parseFloat(selectedRule.percentage);
            const tdsRuleId = selectedRule.id;
            
            const response = await api.put(
                `/invoices/${editingInvoice.id}/tds`,
                { 
                    tdsRuleId: tdsRuleId,
                    tdsRuleDescription: selectedRule.description,  // ← This is missing!
                    tdsPercentage: tdsPercentage
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    withCredentials: true
                }
            );

            // Update the local state with the updated invoice from the backend
            setApprovals(prevApprovals => 
                prevApprovals.map(inv => {
                    if (inv.id === editingInvoice.id) {
                        return { 
                            ...inv, 
                            ...response.data,
                            tdsRuleId: tdsRuleId,
                            tdsPercentage: tdsPercentage,
                            tdsAmount: response.data.tdsAmount || 0,
                            invoicePayable: response.data.invoicePayable || 0
                        };
                    }
                    return inv;
                })
            );
            
            toast.success('TDS tax rate updated successfully for invoice');
            setIsEditModalOpen(false);
        } catch (error) {
            console.error('Error updating TDS:', {
                error: error,
                response: error.response?.data
            });
            toast.error(error.response?.data?.message || 'Failed to update TDS tax rate for invoice');
        } finally {
            setIsUpdating(false);
        }
    };

    // Handle approve/reject action
    const handleStatusUpdate = async (id, action) => {
        const isApproved = action === 'APPROVE';
        const actionText = isApproved ? 'approve' : 'reject';
        
        // Show confirmation toast with action buttons
        const shouldProceed = await new Promise((resolve) => {
            const toastId = toast.info(
                <div className="flex flex-col space-y-4 p-2">
                    <p className="text-gray-800">Confirm Invoice {isApproved ? 'Approval' : 'Rejection'}: This action will {isApproved ? 'move the invoice to payment processing' : 'reject the invoice and require resubmission'}.</p>
                    <div className="flex justify-end space-x-3">
                        <button 
                            className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                            onClick={() => {
                                toast.dismiss(toastId);
                                resolve(false);
                            }}
                        >
                            Cancel
                        </button>
                        <button 
                            className={`px-4 py-2 text-sm text-white rounded-md transition-colors ${
                                isApproved 
                                    ? 'bg-green-600 hover:bg-green-700' 
                                    : 'bg-red-600 hover:bg-red-700'
                            }`}
                            onClick={() => {
                                toast.dismiss(toastId);
                                resolve(true);
                            }}
                        >
                            {isApproved ? 'Approve' : 'Reject'}
                        </button>
                    </div>
                </div>,
                {
                    closeButton: false,
                    closeOnClick: false,
                    draggable: false,
                    autoClose: false,
                    position: 'top-center',
                    className: 'w-full max-w-md',
                }
            );
        });

        if (!shouldProceed) {
            return;
        }

        try {
            // Use the correct endpoint based on the action
            const endpoint = isApproved 
                ? `/invoices/${id}/status/approve`
                : `/invoices/${id}/status/reject`;
                
            const { data: updatedInvoice } = await api.put(endpoint);

            if (updatedInvoice) {
                // Update the local state to reflect the new status from the server
                setApprovals(prevApprovals => 
                    prevApprovals.map(invoice => 
                        invoice.id === id 
                            ? { ...invoice, status: updatedInvoice.status?.toLowerCase() || (isApproved ? 'approved' : 'rejected') } 
                            : invoice
                    )
                );
                
                // Show success message
                toast.success(`Invoice ${actionText}d and moved to payment queue!`);
                
                // Refresh the list to show the updated status
                fetchApprovals();
                
                // Show success message with action to go to payments
                if (isApproved) {
                    toast.success(
                        <div>
                            <p>Invoice approved and ready for payment processing!</p>
                            <div className="mt-2">
                                <button 
                                    className="text-blue-600 hover:underline text-sm"
                                    onClick={() => navigate('payments')}
                                >
                                    Process Payment Now
                                </button>
                            </div>
                        </div>,
                        { autoClose: 5000 }
                    );
                }
            } else {
                throw new Error(`Failed to ${actionText} invoice: No data returned from server`);
            }
        } catch (error) {
            console.error('Error updating invoice status:', error);
            const errorMessage = error.response?.data?.message || error.message;
            
            if (error.response?.status === 403) {
                toast.error('Access denied: You do not have permission to perform this action');
            } else {
                toast.error(`Failed to ${actionText} invoice: ${errorMessage}`);
            }
        }
    };

    const handleViewInvoice = async (invoice) => {
        // Check if the required props were passed in
        if (typeof setSelectedInvoice !== 'function' || typeof navigate !== 'function') {
            console.error('ApprovalPage is missing required props: setSelectedInvoice or navigate.');
            return;
        }

        try {
            setLoading(true);
            // Fetch the complete invoice details from the backend
            const response = await api.get(`/invoices/${invoice.id}`);
            const invoiceData = response.data;
            
            // Get the vendor name from the map
            const vendorName = vendorMap[invoiceData.vendorId] || 
                             (invoiceData.vendorName ? invoiceData.vendorName : 
                             (invoiceData.vendorId ? `ID: ${invoiceData.vendorId}` : 'N/A'));
            
            // Update the selected invoice with complete data
            setSelectedInvoice({
                ...invoiceData,
                vendorName, // Add the resolved vendor name
                // Ensure all required fields have values
                subtotal: invoiceData.subtotal || 0,
                tax: invoiceData.tax || 0,
                tdsAmount: invoiceData.tdsAmount || 0,
                totalAmount: invoiceData.totalAmount || (invoiceData.amount || 0),
                invoicePayable: invoiceData.invoicePayable || 
                              (invoiceData.invoicePayableValue || 
                              ((invoiceData.totalAmount || (invoiceData.amount || 0)) - (invoiceData.tdsAmount || 0)))
            });
            
            // Navigate to the approval detail page
            navigate('approval-detail');
        } catch (error) {
            console.error('Error fetching invoice details:', error);
            toast.error('Failed to load invoice details. Please try again.');
            // Fallback to using the existing data if available
            const vendorName = vendorMap[invoice.vendorId] || 
                             (invoice.vendorName ? invoice.vendorName : 
                             (invoice.vendorId ? `ID: ${invoice.vendorId}` : 'N/A'));
            
            setSelectedInvoice({
                ...invoice,
                vendorName,
                // Ensure all required fields have values with fallbacks
                subtotal: invoice.subtotal || 0,
                tax: invoice.tax || 0,
                tdsAmount: invoice.tdsAmount || 0,
                totalAmount: invoice.totalAmount || (invoice.amount || 0),
                invoicePayable: invoice.invoicePayable || 
                              (invoice.invoicePayableValue || 
                              ((invoice.totalAmount || (invoice.amount || 0)) - (invoice.tdsAmount || 0)))
            });
            navigate('invoice-detail');
        } finally {
            setLoading(false);
        }
    };

    // Format currency values with commas and 2 decimal places
    const formatCurrency = (amount) => {
        if (amount == null) return '-';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    // Format date strings to a more readable format
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
             if (isNaN(date.getTime())) return 'Invalid Date';
            const day = date.getDate();
            let daySuffix = 'th';
            if (day === 1 || day === 21 || day === 31) daySuffix = 'st';
            else if (day === 2 || day === 22) daySuffix = 'nd';
            else if (day === 3 || day === 23) daySuffix = 'rd';
            return `${day}${daySuffix} ${date.toLocaleDateString('en-IN', { month: 'short' })} ${date.getFullYear()}`;
        } catch (e) {
            return 'Invalid Date';
        }
    };

    // Filter options - only pending approvals filter
    const filterOptions = [
        { value: 'PENDING_APPROVAL', label: 'Pending Approval' }
    ];

    // Table headers matching the image
    const tableHeaders = [
        <div key="invoice-header" className="sticky left-0 bg-white z-10 pl-2 pr-4 -ml-2">Invoice #</div>,
        'Date Recvd',
        'Invoice Date',
        'Due Date',
        'Vendor Name',
        'Subtotal',
        'Tax',
        'Total',
        'TDS',
        'Invoice Payable',
        'Actions' // Actions column for Approve/Reject/View
    ];  

    // Table data mapping matching new headers
    const tableData = React.useMemo(() => {
        return filteredApprovals.map(approval => {
            const vendorName = vendorMap[approval.vendorId] || 'Loading...';
            const invoicePayable = approval.invoicePayableValue || 0;

            return [
                <div key={`${approval.id}-invoice`} className="sticky left-0 bg-white z-10 pl-2 pr-4 -ml-2">
                    {approval.invoiceNumber || 'N/A'}
                </div>,
                approval.dateReceived ? formatDate(approval.dateReceived) : 'N/A',
                approval.invoiceDate ? formatDate(approval.invoiceDate) : 'N/A',
                approval.dueDate ? formatDate(approval.dueDate) : 'N/A',
                <div className="min-w-[150px] max-w-[250px] break-words whitespace-normal">
                    {vendorName}
                </div>,
                approval.subtotal !== undefined && approval.subtotal !== null ? formatCurrency(approval.subtotal) : 'N/A',
                approval.tax !== undefined && approval.tax !== null ? formatCurrency(approval.tax) : 'N/A',
                formatCurrency(approval.amount),
                ['pending_approval', 'pending'].includes(approval.status?.toLowerCase()) ? (
                    <div 
                        key={`${approval.id}-tds`} 
                        className="text-blue-600 hover:underline cursor-pointer transition-colors duration-200 inline-flex flex-col"
                        onClick={() => handleEditClick(approval)}
                        title="Click to update TDS section"
                    >
                        <span className="text-xs text-gray-400 font-normal leading-tight mb-0.5">
                            {getTdsRuleDescription(approval.tdsRule)}
                        </span>
                        <span className="font-medium whitespace-nowrap">
                            {approval.tdsAmount != null ? `${formatCurrency(approval.tdsAmount)} (${approval.tdsPercentage || 0}%)` : 'N/A'}
                        </span>
                    </div>
                ) : (
                    <div key={`${approval.id}-tds`} className="text-gray-700 inline-flex flex-col">
                        <span className="text-xs text-gray-400 font-normal leading-tight mb-0.5">
                            {getTdsRuleDescription(approval.tdsRule)}
                        </span>
                        <span className="whitespace-nowrap">
                            {approval.tdsAmount != null ? `${formatCurrency(approval.tdsAmount)} (${approval.tdsPercentage || 0}%)` : 'N/A'}
                        </span>
                    </div>
                ),
                <div key={`${approval.id}-payable`} className="font-semibold text-blue-600">
                    {formatCurrency(invoicePayable)}
                </div>,
                <div key={`actions-${approval.id}`} className="flex space-x-2 items-center">
                    {['pending_approval', 'pending'].includes(approval.status?.toLowerCase()) ? (
                        <>
                            <Button
                                size="sm"
                                variant="success"
                                onClick={() => handleStatusUpdate(approval.id, 'APPROVE')}
                                title="Approve this invoice"
                            >
                                <I.Check className="w-4 h-4 mr-1" />
                                Approve
                            </Button>
                            <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleStatusUpdate(approval.id, 'REJECT')}
                                title="Reject this invoice"
                            >
                                <I.X className="w-4 h-4 mr-1" />
                                Reject
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewInvoice(approval)}
                                title="View Invoice Details"
                            >
                                <I.Eye className="w-4 h-4 mr-1" />
                                View
                            </Button>
                        </>
                    ) : (
                        <div className="flex items-center space-x-2">
                            <Badge 
                                variant={
                                    ['approved', 'approve'].includes(approval.status?.toLowerCase()) ? 'success' : 
                                    ['rejected', 'reject'].includes(approval.status?.toLowerCase()) ? 'destructive' : 'default'
                                }
                                className="px-3 py-1.5 text-sm capitalize"
                            >
                                {approval.status ? approval.status.toLowerCase() : 'Unknown Status'}
                            </Badge>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewInvoice(approval)}
                                title="View Invoice Details"
                            >
                                <I.Eye className="w-4 h-4 mr-1" />
                                View
                            </Button>
                        </div>
                    )}
                </div>
            ];
        });
    }, [filteredApprovals, vendorMap, handleStatusUpdate, handleEditClick, handleViewInvoice]); // Added handleViewInvoice

    // TDS Rule Options for Select
    const tdsRuleOptions = useMemo(() => {
        if (loadingTdsRules) return [{ value: '', label: 'Loading TDS sections...' }];
        if (!tdsRules || tdsRules.length === 0) return [{ value: '', label: 'No TDS sections available' }];
        return [
            { value: '', label: 'Select TDS Section...' },
            ...tdsRules.map(rule => ({
                value: rule.id,
                label: rule.description,
                percentage: rule.percentage
            }))
        ];
    }, [tdsRules, loadingTdsRules]);

    return (
        <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-gray-50 min-h-screen">
            <PageHeader
                title="Manage Invoice Approvals"
                subtitle="Review and manage invoices"
                actions={[
                    <Button key="refresh" variant="outline" onClick={fetchApprovals} disabled={loading}>
                        <I.RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin': ''}`} />
                        Refresh
                    </Button>,
                ]}
            />

            {/* Search, Filters, and Table Card */}
            <Card className="shadow-sm">
                 <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 border-b border-gray-200">
                     {/* Search Bar Container */}
                     <div className="flex-1 min-w-0 relative">
                         <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                             <I.Search className="w-4 h-4 text-gray-400" />
                         </span>
                         <input
                            type="text"
                            placeholder="Search by Invoice # or Vendor Name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-10 py-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                         {searchTerm && (
                            <button
                                type="button"
                                onClick={() => setSearchTerm('')} // Clear search term state
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                                title="Clear search"
                            >
                                <I.X className="h-4 w-4" />
                            </button>
                        )}
                    </div>

                    {/* Filter Container */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="w-full md:w-auto md:min-w-[200px]">
                            <Select
                                id="statusFilter"
                                value={filterStatus}
                                onChange={handleFilterChange}
                                leftIcon={<I.Filter className="w-4 h-4 text-gray-400" />}
                                options={filterOptions} // Use defined filter options
                            />
                        </div>

                        {/* Clear Button Logic */}
                         {searchTerm && ( // Show clear only if search has content
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setSearchTerm('');
                                    setFilterStatus('PENDING_APPROVAL'); // Reset to pending approval
                                    setCurrentPage(0);
                                }}
                                title="Clear search"
                            >
                                <I.X className="w-4 h-4 mr-1" />
                                Clear
                            </Button>
                        )}
                    </div>
                </div>

                {/* Table Area */}
                <div className="relative overflow-x-auto" style={{
                    '--scrollbar-width': '8px',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#cbd5e0 #f7fafc',
                }}>
                    {loading && approvals.length === 0 ? ( // Show loader only on initial load
                         <div className="flex items-center justify-center py-16">
                            <LoadingSpinner size="lg" />
                            <span className="ml-3 text-lg text-gray-500">Loading approvals...</span>
                        </div>
                    ) : error ? (
                        <div className="text-center py-16 px-4">
                            <div className="text-red-500 mb-4"> <I.AlertTriangle className="w-12 h-12 mx-auto mb-3" /> </div>
                            <h3 className="text-lg font-semibold text-gray-700 mb-2">Invoice Approval System Error</h3>
                            <p className="text-gray-500 text-sm mb-4">{error}</p>
                            <Button onClick={fetchApprovals} variant="outline">Try Again</Button>
                        </div>
                    ) : filteredApprovals.length === 0 ? ( // Check filtered data for empty state
                        <div className="text-center py-16 px-4">
                           <I.SearchX className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Approvals Found</h3>
                             <p className="text-gray-500 text-sm">
                                 {searchTerm
                                    ? "No approvals match your current search criteria."
                                    : "There are no pending approvals available."}
                           </p>
                        </div>
                    ) : (
                        <Table
                            headers={tableHeaders}
                            data={tableData}
                            loading={loading}
                            className="min-w-full"
                        />
                    )}
                </div>

                {/* Pagination */}
                 {totalPages > 1 && !error && (
                    <div className="px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-2">
                        <div className="text-xs sm:text-sm text-gray-500">
                             Showing <span className="font-medium">{(currentPage * pageSize) + (filteredApprovals.length > 0 ? 1 : 0)}</span>-
                             <span className="font-medium">{Math.min((currentPage * pageSize) + filteredApprovals.length, totalApprovals)}</span> of{' '}
                             <span className="font-medium">{totalApprovals}</span> results
                         </div>
                         <Pagination
                            currentPage={currentPage + 1} // Convert to 1-based for display
                            totalPages={totalPages}
                            onPageChange={handlePageChange} // Use the correct handler
                        />
                    </div>
                )}
            </Card>
            
            {/* Edit TDS Modal */}
            <Modal
                isOpen={isEditModalOpen}
                onClose={() => !isUpdating && setIsEditModalOpen(false)}
                title="Update TDS Percentage"
            >
                <form onSubmit={handleTdsUpdate} className="space-y-4">
                    {editingInvoice && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Invoice #{editingInvoice.invoiceNumber}
                                </label>
                                <p className="text-sm text-gray-500 mb-4">
                                    Vendor: {vendorMap[editingInvoice.vendorId] || 'N/A'}
                                </p>
                            </div>
                            
                            <div>
                                <label htmlFor="tdsRule" className="block text-sm font-medium text-gray-700 mb-1">
                                    TDS Section
                                </label>
                                <div className="w-full">
                                    <Select
                                        id="tdsRule"
                                        value={tdsFormData.tdsRuleId}
                                        onChange={(e) => {
                                            const ruleId = e.target.value;
                                            const rule = tdsRules.find(r => r.id.toString() === ruleId.toString());
                                            setTdsFormData(prev => ({ 
                                                ...prev, 
                                                tdsRuleId: ruleId, 
                                                tdsPercentage: rule?.percentage || 0 
                                            }));
                                        }}
                                        options={tdsRuleOptions}
                                        disabled={loadingTdsRules || isUpdating}
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => setIsEditModalOpen(false)}
                                    disabled={isUpdating}
                                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isUpdating || loadingTdsRules} // Also disable if rules are loading
                                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                >
                                    {isUpdating ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <I.Save className="w-4 h-4 mr-2" />
                                            Update TDS
                                        </>
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </form>
            </Modal>
        </div>
    );
};

export default ApprovalPage;